import { lazy, Suspense, useCallback, useRef, useState } from 'react';

import { type AccountApi } from './accountPort';
import { Logo, Wordmark } from './Logo';
import { type ReportApi } from './reportPort';
import { colour, space, text } from './theme';
import { useDismissOnOutside } from './useDismissOnOutside';

/*
 * Loaded when it is opened, not before.
 *
 * The dialogue pulls in the account client and everything it touches, and the overwhelming
 * majority of sessions never open it — the drawing tool needs no account. Splitting it keeps that
 * weight off the first paint of a teacher who only ever draws.
 */
const AccountDialog = lazy(() =>
  import('./AccountDialog').then((module) => ({ default: module.AccountDialog }))
);

/** Cùng lý do: hộp thoại báo lỗi chỉ được tải khi có người thực sự bấm vào nó. */
const ReportDialog = lazy(() =>
  import('./ReportDialog').then((module) => ({ default: module.ReportDialog }))
);

/**
 * File actions and the autosave indicator.
 *
 * Presentation only. It reports intent and shows the status it is handed; the composition
 * root owns the repository, the codec and the autosave timer.
 */

/**
 * Written as a bare union rather than a const-object pair because this file exports a
 * component: `react-refresh/only-export-components` rejects a value export alongside it,
 * and fast refresh genuinely does stop working when one is added.
 */
export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'failed' | 'conflict';

/** Picture formats a drawing can be saved as. Kept as a union for the same reason. */
export type PictureFormat = 'png' | 'svg' | 'pdf' | 'jpeg';

/**
 * Paper for the print entries.
 *
 * A separate choice from the format, because "PDF" and "a printed A4 sheet" are different
 * things: one is a picture for a document, the other is paper. Offering them as one list of
 * *outcomes* is what stops a user picking PDF and wondering why it is not page-sized.
 */
export type PrintPaper = 'a4' | 'a4-landscape' | 'letter' | 'a4-tiled';

export interface FileBarProps {
  readonly saveState: SaveState;
  readonly lastSavedAt: number | null;
  readonly error: string | null;
  readonly warnings: readonly string[];
  readonly onExport: () => void;
  /** `aspect` is the width ÷ height to pad the file to; `undefined` keeps the drawing's shape. */
  readonly onExportPicture: (
    format: PictureFormat,
    onlySelection: boolean,
    aspect: number | undefined
  ) => void;
  /** How many objects are selected, which decides whether a partial export is offered. */
  readonly selectionCount: number;
  readonly onPrint: (paper: PrintPaper) => void;
  /** Opens the written-construction panel. */
  readonly onOpenScript: () => void;
  readonly onImport: (file: File) => void;
  /** Places a picture on the canvas. Separate from `onImport`, which replaces the drawing. */
  readonly onImportImage: (file: File) => void;
  /** Empties the drawing. Undoable in one step. */
  readonly onClear: () => void;
  readonly onSaveNow: () => void;
  /** Omitted when the deployment has no accounts; the button is then not shown. */
  readonly accountApi?: AccountApi | undefined;
  /** Omitted when the deployment has no database to keep reports in. */
  readonly reportApi?: ReportApi | undefined;
  /** Bản vẽ hiện tại dạng JSON, để gửi kèm báo lỗi. Chỉ gọi khi hộp thoại mở ra. */
  readonly snapshot?: (() => string) | undefined;
}

/**
 * The picture formats, in the order a teacher is likely to want them.
 *
 * Each carries the *use* it is for rather than a description of the file structure: nobody
 * chooses between a raster and a vector, they choose between a slide and a worksheet.
 */
const PRINT_PAPERS: readonly { value: PrintPaper; label: string; hint: string }[] = [
  { value: 'a4', label: 'A4', hint: 'One portrait sheet, scaled to fit.' },
  { value: 'a4-landscape', label: 'A4 landscape', hint: 'One landscape sheet, scaled to fit.' },
  { value: 'letter', label: 'US Letter', hint: 'One portrait sheet, scaled to fit.' },
  {
    value: 'a4-tiled',
    label: 'A4, tiled',
    hint: 'Full size across as many sheets as it takes, to tape together.',
  },
];

/**
 * The shapes a picture can be padded to, as width ÷ height.
 *
 * Both orientations of each ratio, because a tall figure padded to a landscape shape is mostly
 * paper — the point of choosing a shape is that it suits what is being framed.
 */
const FRAME_SHAPES: readonly { label: string; value: number | undefined; hint: string }[] = [
  { label: 'Tự do', value: undefined, hint: 'Khung ôm sát hình, không thêm lề.' },
  { label: '1:1', value: 1, hint: 'Vuông.' },
  { label: '4:3', value: 4 / 3, hint: 'Ngang, tỉ lệ quen thuộc nhất cho hình trong đề.' },
  { label: '3:4', value: 3 / 4, hint: 'Dọc, cho hình cao.' },
  { label: '3:2', value: 3 / 2, hint: 'Ngang, rộng hơn 4:3.' },
  { label: '2:3', value: 2 / 3, hint: 'Dọc, cao hơn 3:4.' },
  { label: '16:9', value: 16 / 9, hint: 'Cho slide trình chiếu.' },
];

const PICTURE_FORMATS: readonly { value: PictureFormat; label: string; hint: string }[] = [
  {
    value: 'png',
    label: 'PNG',
    hint: 'For slides and documents. Twice the resolution, white background.',
  },
  {
    value: 'svg',
    label: 'SVG',
    hint: 'Vector, transparent. Scales to any size without going soft.',
  },
  { value: 'pdf', label: 'PDF', hint: 'Vector, page-sized. For printing a worksheet.' },
  {
    value: 'jpeg',
    label: 'JPEG',
    hint: 'Smaller than PNG, and lossy. Only when something demands it.',
  },
];

export function FileBar({
  saveState,
  lastSavedAt,
  error,
  warnings,
  onExport,
  onExportPicture,
  selectionCount,
  onPrint,
  onOpenScript,
  onImport,
  onImportImage,
  onClear,
  onSaveNow,
  accountApi,
  reportApi,
  snapshot,
}: FileBarProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [pictureMenu, setPictureMenu] = useState(false);
  /*
   * Whether the next picture is of the selection rather than of the drawing.
   *
   * A switch inside the menu rather than four more entries beside the four formats. The two
   * questions a user is answering are *what* and *in which format*, and doubling the list to
   * eight makes them read every line to find the pair they want. It also resets whenever the
   * menu is closed, so the choice cannot linger and surprise the next export.
   */
  const [onlySelection, setOnlySelection] = useState(false);
  /**
   * The shape the next picture is padded to. `undefined` is the drawing's own shape.
   *
   * Kept while the menu stays open, and reset with it — a teacher exporting a set of figures
   * wants the same shape each time, and a shape that survived into an unrelated export later
   * would be the surprise the reset below exists to prevent.
   */
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const [accountOpen, setAccountOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  /*
   * Closed by a press anywhere else — including on the drawing, which is where a user is
   * certain to click next and where the menu used to sit open until they came back and pressed
   * `Xuất ảnh` again.
   *
   * `onlySelection` is reset with it for the reason given above: a choice made inside a menu
   * that is no longer open must not linger and surprise the next export.
   */
  const pictureRef = useRef<HTMLDivElement | null>(null);
  const closePictureMenu = useCallback(() => {
    setPictureMenu(false);
    setOnlySelection(false);
  }, []);
  useDismissOnOutside(pictureRef, closePictureMenu, pictureMenu);

  const canExportSelection = selectionCount > 0;
  const partial = canExportSelection && onlySelection;

  return (
    <div
      className="es-panel"
      style={{
        /*
         * Placed by the shell, not by itself.
         *
         * This bar and the lesson bar both used to position themselves at `left: 50%,
         * top: 12` — the same pixel — so whichever React rendered last simply covered the
         * other. The lesson bar won, and it sat across the middle of the file controls. Two
         * components each claiming the same absolute slot cannot be made to agree; the fix is
         * for neither to claim it.
         */
        display: 'flex',
        alignItems: 'center',
        gap: space.tight,
        pointerEvents: 'auto',
        padding: space.snug,
        font: text.body,
        /*
         * Above the bar below it, so the export menu can open over that bar.
         *
         * This is not the same as the `z-index: 10` the menu already carries, and that is why
         * the menu was appearing *underneath* the lesson bar despite it. `.es-panel` sets
         * `backdrop-filter`, and a backdrop filter creates a **stacking context** — so the
         * menu's z-index is resolved entirely *inside this bar* and cannot lift it past
         * anything outside. What the shell then sees is two sibling stacking contexts painted
         * in tree order, and the lesson bar is written second.
         *
         * So the thing that has to rise is the bar, not the menu. `position` is here only
         * because `z-index` does nothing without it; the bar is still placed by the shell.
         *
         * Deliberately not applied to the shell's whole column. The script panel is a centred
         * dialogue rendered after it and must stay on top — raising the column would put this
         * bar across a dialogue, which is the same defect moved somewhere less obvious.
         */
        position: 'relative',
        zIndex: 1,
      }}
    >
      {/*
        The brand, at the head of the bar it belongs to.

        Here rather than in a corner of its own, for the reason the whole placement question
        turns on: a logo pinned to the canvas would be sitting *on the drawing*, and the drawing
        is the product. This bar already floats over the page, so the mark costs no space that
        was not already spent — and in presentation mode the bar is not rendered at all, so the
        figure is shown without a brand across it, which is how it should be shown.
      */}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          paddingRight: space.snug,
          marginRight: space.hair,
          borderRight: '1px solid var(--es-hairline)',
        }}
      >
        <Logo size={22} />
        <span className="es-brand-name">
          <Wordmark size={15} />
        </span>
      </span>

      {/*
        Vietnamese, like the rest of the interface, and *specific* rather than short.
        "Export" and "Picture" sat next to each other reading as near-synonyms — and once an
        image *import* button joined them the bar had three controls a user could reasonably
        read as "the picture one". The report was simply "where is the export-image button".
        Naming the object each one acts on removes the guesswork.
      */}
      <button
        type="button"
        onClick={onExport}
        className="es-btn"
        title="Lưu bản vẽ thành tệp .json — mở lại được và sửa tiếp được"
      >
        Lưu tệp
      </button>
      {/*
        Account, in the bar rather than over the drawing — the same reasoning as the mark beside
        it. A teacher who never signs in sees one more button and loses no canvas.
      */}
      {accountApi !== undefined && (
        <button
          type="button"
          onClick={() => {
            setAccountOpen(true);
          }}
          className="es-btn"
          title="Đăng nhập — chỉ cần cho tính năng tạo hình từ đề bài"
        >
          Tài khoản
        </button>
      )}
      {/*
        Hướng dẫn, mở ở tab mới.
        Một trang tĩnh chứ không phải hộp thoại, vì nó để **tra trong lúc đang vẽ**: một hộp thoại
        che mất hình và đóng lại là mất chỗ đang đọc, còn một tab thì bấm qua bấm lại được.
      */}
      <a
        href="/huong-dan-su-dung.html"
        target="_blank"
        rel="noreferrer"
        className="es-btn"
        title="Hướng dẫn sử dụng và bảng tra phím tắt — mở ở tab mới"
      >
        Hướng dẫn
      </a>
      {/*
        Báo lỗi, ngay cạnh Tài khoản.
        Ở thanh trên cùng chứ không giấu trong một menu, vì một nút báo lỗi phải tìm thấy được
        vào **đúng lúc đang bực** — người vừa gặp lỗi không đi lục menu, họ đóng tab.
      */}
      {reportApi !== undefined && snapshot !== undefined && (
        <button
          type="button"
          onClick={() => {
            setReportOpen(true);
          }}
          className="es-btn"
          title="Báo lỗi — gửi kèm bản vẽ đang mở để lỗi dựng lại được"
        >
          Báo lỗi
        </button>
      )}
      {reportOpen && reportApi !== undefined && snapshot !== undefined && (
        <Suspense fallback={null}>
          <ReportDialog
            api={reportApi}
            snapshot={snapshot}
            onClose={() => {
              setReportOpen(false);
            }}
          />
        </Suspense>
      )}
      {accountOpen && accountApi !== undefined && (
        <Suspense fallback={null}>
          <AccountDialog
            api={accountApi}
            onClose={() => {
              setAccountOpen(false);
            }}
          />
        </Suspense>
      )}
      <div ref={pictureRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => {
            setPictureMenu((open) => !open);
            setOnlySelection(false);
          }}
          aria-expanded={pictureMenu}
          className="es-btn"
        >
          Xuất ảnh ▾
        </button>

        {pictureMenu && (
          <div
            className="es-menu"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: space.snug,
              width: 252,
              zIndex: 10,
            }}
          >
            {/*
              Offered only when there *is* a selection.

              A permanently visible switch that does nothing most of the time is a control the
              user has to test to understand. Appearing exactly when it applies — and saying
              how many objects it would take — makes it explain itself.
            */}
            {canExportSelection && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: space.snug,
                  margin: '1px 0 4px',
                  padding: '6px 9px',
                  borderRadius: 'var(--es-radius-xs)',
                  background: partial ? 'var(--es-accent-soft)' : colour.sunken,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={onlySelection}
                  onChange={(event) => {
                    setOnlySelection(event.target.checked);
                  }}
                />
                <span style={{ font: text.body, color: partial ? colour.accentInk : colour.body }}>
                  Chỉ phần đang chọn
                  <span style={{ color: colour.faint, fontWeight: 400 }}>
                    {' '}
                    ({selectionCount} đối tượng)
                  </span>
                </span>
              </label>
            )}

            {/*
              The shape of the file, chosen before the format rather than after.

              A set of figures going into one exam paper wants one shape, and getting there by
              resizing each of them afterwards is both fiddly and visibly uneven. The space is
              *added*, never squeezed out of the drawing — a figure stretched to fit would put an
              ellipse where the reader is being shown a circle.

              `Tự do` stays the default and stays first: a user who has not asked for a shape
              should get the shape of their own drawing.
            */}
            <span className="es-eyebrow" style={{ display: 'block', padding: '5px 9px 3px' }}>
              Tỉ lệ khung ảnh
            </span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 2,
                padding: '0 9px 4px',
              }}
            >
              {FRAME_SHAPES.map((shape) => (
                <button
                  key={shape.label}
                  type="button"
                  onClick={() => {
                    setAspect(shape.value);
                  }}
                  title={shape.hint}
                  className={aspect === shape.value ? 'es-btn es-btn--on' : 'es-btn'}
                  style={{ padding: '5px 0', fontSize: 11 }}
                >
                  {shape.label}
                </button>
              ))}
            </div>

            <span className="es-eyebrow" style={{ display: 'block', padding: '5px 9px 3px' }}>
              {partial ? 'Ảnh phần đang chọn' : 'Ảnh — PNG, SVG, PDF, JPEG'}
            </span>
            {PICTURE_FORMATS.map((format) => (
              <button
                key={format.value}
                type="button"
                onClick={() => {
                  setPictureMenu(false);
                  onExportPicture(format.value, partial, aspect);
                }}
                className="es-menu-item"
              >
                <span style={{ font: text.title, color: colour.ink }}>{format.label}</span>
                <span style={{ display: 'block', font: text.small, color: colour.muted }}>
                  {format.hint}
                </span>
              </button>
            ))}

            <span
              className="es-eyebrow"
              style={{
                display: 'block',
                padding: '8px 9px 3px',
                marginTop: space.tight,
                borderTop: `1px solid ${colour.hairline}`,
              }}
            >
              In — PDF theo khổ giấy
            </span>
            {PRINT_PAPERS.map((paper) => (
              <button
                key={paper.value}
                type="button"
                onClick={() => {
                  setPictureMenu(false);
                  onPrint(paper.value);
                }}
                className="es-menu-item"
              >
                <span style={{ font: text.title, color: colour.ink }}>{paper.label}</span>
                <span style={{ display: 'block', font: text.small, color: colour.muted }}>
                  {paper.hint}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onOpenScript}
        title="Mô tả hình bằng văn bản, hoặc dán kết quả từ AI"
        className="es-btn"
      >
        Text ▸ hình
      </button>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="es-btn"
        title="Mở một tệp .json — thay thế bản vẽ hiện tại"
      >
        Mở tệp
      </button>
      {/*
        A separate button and a separate input, because the two are opposite actions that a
        single picker could not tell apart: importing a document *replaces* the drawing, and
        importing a picture *adds to* it. Sharing one control would eventually lose somebody's
        work to a mis-picked file.
      */}
      <button
        type="button"
        onClick={() => imageInputRef.current?.click()}
        className="es-btn"
        title="Chèn một ảnh vào bản vẽ hiện tại"
      >
        Chèn ảnh
      </button>
      <button type="button" onClick={onSaveNow} className="es-btn">
        Lưu ngay
      </button>

      <span
        className="es-divider"
        style={{ width: 1, height: 18, margin: `0 ${String(space.hair)}px` }}
      />

      {/*
        Set apart by a divider and coloured as what it is.
        A destructive action sitting flush against the everyday ones is a mis-click waiting to
        happen; a divider and a red tint cost nothing and make the hand hesitate. There is no
        confirmation dialogue on purpose — see `clearDocument`: one undo step protects the user
        better than a prompt nobody reads.
      */}
      <button
        type="button"
        className="es-btn es-btn--danger"
        onClick={onClear}
        title="Xoá toàn bộ bản vẽ — Ctrl+Z để lấy lại"
      >
        Xoá hết
      </button>

      <input
        ref={inputRef}
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) onImport(file);
          // Cleared so picking the same file twice fires the change event again.
          event.target.value = '';
        }}
      />

      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) onImportImage(file);
          event.target.value = '';
        }}
      />

      <span
        className="es-divider"
        style={{ width: 1, height: 18, margin: `0 ${String(space.hair)}px` }}
      />
      <SaveIndicator state={saveState} lastSavedAt={lastSavedAt} error={error} />

      {warnings.length > 0 && (
        <span
          title={warnings.join('\n')}
          style={{ color: '#b45309', cursor: 'help', font: text.title }}
        >
          {warnings.length} cảnh báo
        </span>
      )}
    </div>
  );
}

function SaveIndicator({
  state,
  lastSavedAt,
  error,
}: {
  readonly state: SaveState;
  readonly lastSavedAt: number | null;
  readonly error: string | null;
}) {
  const state_ = describe(state, lastSavedAt);
  return (
    <span
      title={error ?? undefined}
      style={{
        display: 'inline-block',
        // A fixed width, because this is the one label on the bar that rewrites itself while
        // the user is working — and every character it gains shoves the whole bar sideways.
        minWidth: 104,
        color: state_.tone,
        font: text.small,
      }}
    >
      {state_.label}
    </span>
  );
}

function describe(state: SaveState, lastSavedAt: number | null): { label: string; tone: string } {
  switch (state) {
    case 'pending':
      return { label: 'Chưa lưu', tone: colour.muted };
    case 'saving':
      return { label: 'Đang lưu…', tone: colour.muted };
    case 'saved':
      return {
        label: lastSavedAt === null ? 'Đã lưu' : `Đã lưu ${formatTime(lastSavedAt)}`,
        tone: '#15803d',
      };
    case 'failed':
      return { label: 'Lưu hỏng', tone: colour.danger };
    // Distinct from a plain failure: a conflict is not retried, because writing over the
    // other version is exactly what the check exists to prevent.
    case 'conflict':
      return { label: 'Đã đổi nơi khác', tone: '#b45309' };
    case 'idle':
      return { label: 'Chưa có thay đổi', tone: colour.faint };
  }
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}
